import { IImageProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPImage, IPText, IPTooltipHost } from 'baseComponents';
import { IPImageFit } from 'baseComponents/Image/IPImage';
import { IPStack } from 'baseComponents/Stack/IPStack';
import PinnedLinkTabs from 'components/Links/PinnedLinkTabs';
import { Info, LearnMore, LearnMoreModes } from 'components/Notification/Info';
import ScenePivot, { IScenePivotItemProps } from 'components/SceneBar/ScenePivot';
import CopyLink from 'components/Utils/CopyLink';
import { globalNavBarHeight } from 'globalConstants';
import {
  backIcon,
  globalPaddingScene,
  globalStackItemStylesPaddingScene,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import ResourceLink from 'models/resourceLink';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Config from 'services/Config/configService';

interface ISceneBarProps {
  title: string;
  subtitle: string;
  // optional explanation of the scene, shown as an info icon behind the subtitle
  info?: string | JSX.Element;
  // optional help article, shown as a Learn link behind the title
  learnMoreLink?: string;
  image: string;
  pivotItems?: IScenePivotItemProps[];
  selectedPivotKey?: string;
  pinnedLinks?: ResourceLink[];
  selectedPinnedLink?: ResourceLink;
  onSelectPivot?: (item: IScenePivotItemProps) => void;
  //query parameters of the current URL that survive a pivot click, e.g. frameworkView on a detail scene
  preserveQueryParams?: string[];
  onSelectPinnedLink?: (link: ResourceLink) => void;
  linkUrl?: string;
  showBackButton?: boolean;
  backButtonUrl?: string;
  backButtonClassName?: string;
  children?: ReactNode;
}

const SceneBar = (props: ISceneBarProps) => {
  const { t } = useTranslation(['translation', 'norms']);
  const appContext = useContext(AppContext);
  const history = useHistory();

  const imagePropsSceneTitle: IImageProps = {
    src: `${Config.getImageURL()}/${props.image}`,
    imageFit: IPImageFit.centerContain,
    width: globalNavBarHeight,
    height: globalNavBarHeight,
  };

  return (
    <IPStack>
      <IPStack horizontal wrap verticalAlign="center" styles={{ root: { minHeight: 64 } }}>
        <IPStack.Item>
          <IPStack
            horizontal
            verticalAlign="center"
            styles={globalStackItemStylesPaddingScene}
            tokens={globalStackTokensGapSmall}
          >
            <IPStack.Item>
              <IPImage {...imagePropsSceneTitle}></IPImage>
            </IPStack.Item>
            <IPStack.Item>
              <IPStack horizontal verticalAlign="center">
                {props.showBackButton && (
                  <IPStack.Item>
                    <IPTooltipHost content={t('translation:NavMain.GoBack')}>
                      <IPIconButton
                        iconProps={backIcon}
                        onClick={() => {
                          if (props.backButtonUrl) {
                            history.push(props.backButtonUrl);
                          }
                        }}
                      />
                    </IPTooltipHost>
                  </IPStack.Item>
                )}
                <IPText styles={globalTextStylesBold} block nowrap variant="large">
                  {props.title}
                </IPText>
                {props.learnMoreLink && (
                  <IPStack.Item styles={{ root: { paddingLeft: 12 } }}>
                    <LearnMore learnMoreLink={props.learnMoreLink} mode={LearnMoreModes.Learn} />
                  </IPStack.Item>
                )}
              </IPStack>
              {!appContext.isMobileView && props.subtitle && (
                <IPStack
                  horizontal
                  tokens={globalStackTokensGapSmall}
                  styles={{ root: { paddingLeft: props.showBackButton ? 8 : undefined } }}
                >
                  <IPStack.Item>
                    <IPText block variant="small">
                      {props.subtitle}
                    </IPText>
                  </IPStack.Item>
                  {props.info && (
                    <IPStack.Item>
                      <Info message={props.info} />
                    </IPStack.Item>
                  )}
                  {props.linkUrl && (
                    <IPStack.Item>
                      <CopyLink url={props.linkUrl}></CopyLink>
                    </IPStack.Item>
                  )}
                </IPStack>
              )}
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        {props.pivotItems && (
          <IPStack.Item grow={1}>
            <ScenePivot
              items={props.pivotItems}
              selectedKey={props.selectedPivotKey}
              onSelectPivot={props.onSelectPivot}
              preserveQueryParams={props.preserveQueryParams}
            />
          </IPStack.Item>
        )}
        {props.pinnedLinks && props.onSelectPinnedLink && (
          <IPStack.Item grow>
            <PinnedLinkTabs
              links={props.pinnedLinks}
              selectedLink={props.selectedPinnedLink}
              onClick={props.onSelectPinnedLink}
            ></PinnedLinkTabs>
          </IPStack.Item>
        )}
        {props.children && (
          <IPStack.Item grow={5}>
            <IPStack
              horizontal
              tokens={globalStackTokensGapSmall}
              styles={{ root: { paddingLeft: globalPaddingScene } }}
            >
              {props.children}
            </IPStack>
          </IPStack.Item>
        )}
      </IPStack>
    </IPStack>
  );
};

export default SceneBar;
